import { api } from '../../app/api';

import type { OrdersWithIssuesResponse, Thread, ThreadTarget } from './threads.schema';
import {
    OrdersWithIssuesResponseSchema,
    ThreadListResponseSchema,
    ThreadResponseSchema,
} from './threads.schema';

const targetTag = (t: ThreadTarget) => `${t.targetType}:${t.targetId}`;

interface PostThreadArgs {
    target: ThreadTarget;
    parentId?: number | null;
    message: string;
    params?: Record<string, unknown> | null;
}

const threadsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listThreads: builder.query<Thread[], ThreadTarget>({
            query: ({ targetType, targetId }) => ({
                url: '/v2/threads',
                method: 'GET',
                params: { targetType, targetId },
            }),
            transformResponse: (raw: unknown) => ThreadListResponseSchema.parse(raw).data,
            providesTags: (result, _err, target) => {
                const id = targetTag(target);
                const list = [{ type: 'ThreadList' as const, id }];
                if (!result) return list;
                return [
                    ...list,
                    ...result.map((t) => ({ type: 'Thread' as const, id: t.id })),
                ];
            },
        }),

        postThread: builder.mutation<Thread, PostThreadArgs>({
            query: ({ target, parentId, message, params }) => ({
                url: '/v2/threads',
                method: 'POST',
                body: {
                    targetType: target.targetType,
                    targetId: target.targetId,
                    parentId: parentId ?? null,
                    message,
                    params: params ?? null,
                },
            }),
            transformResponse: (raw: unknown) => ThreadResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { target }) => [
                { type: 'ThreadList' as const, id: targetTag(target) },
                // Bumping Order.issues; refresh the dashboard widget.
                { type: 'OrdersWithIssues' as const, id: 'LIST' },
            ],
        }),

        listOrdersWithIssues: builder.query<OrdersWithIssuesResponse, { page?: number; limit?: number } | void>({
            query: (args) => {
                const { page = 1, limit = 10 } = args ?? {};
                return {
                    url: '/dashboard/orders-with-issues',
                    method: 'GET',
                    params: { page, limit },
                };
            },
            transformResponse: (raw: unknown) => OrdersWithIssuesResponseSchema.parse(raw),
            providesTags: [{ type: 'OrdersWithIssues' as const, id: 'LIST' }],
        }),
    }),
});

export const {
    useListThreadsQuery,
    usePostThreadMutation,
    useListOrdersWithIssuesQuery,
} = threadsApi;
